import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Clock, MapPin, ShieldAlert } from 'lucide-react';
import { useSEO, slugify, SITE_URL } from '@/lib/seo';
import { DishImage } from '@/components/DishImage';
import { Markdown } from '@/lib/markdown';
import { getPostBySlug, POSTS } from '@/lib/blogPosts';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { Breadcrumbs } from '@/components/Breadcrumbs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (d) =>
  new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getPostBySlug(slug);
  const [linkedRecipes, setLinkedRecipes] = useState([]);

  useEffect(() => {
    if (!post?.relatedRecipes?.length) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/recipes`);
        if (cancelled) return;
        const set = new Set(post.relatedRecipes);
        const matched = data
          .filter((r) => set.has(slugify(r.name)))
          .filter((r, i, arr) => arr.findIndex((x) => slugify(x.name) === slugify(r.name)) === i)
          .slice(0, 8);
        setLinkedRecipes(matched);
      } catch (err) {
        /* the article reads fine without the recipe cards */
      }
    })();
    return () => { cancelled = true; };
  }, [post]);

  useSEO({
    title: post ? `${post.title} | Masala Spin` : 'Blog Post | Masala Spin',
    description: post ? post.description : 'Indian recipe guide on Masala Spin.',
    canonical: post ? `${SITE_URL}/blog/${post.slug}` : `${SITE_URL}/blog`,
    image: post?.cover ? `${SITE_URL}${post.cover}` : undefined,
    jsonLd: post
      ? {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.description,
          datePublished: post.date,
          dateModified: post.dateModified || post.date,
          image: post.cover ? [`${SITE_URL}${post.cover}`] : undefined,
          keywords: post.keywords,
          wordCount: String(post.body || '').split(/\s+/).length,
          inLanguage: 'en',
          author: { '@type': 'Organization', name: 'Masala Spin', url: SITE_URL },
          publisher: {
            '@type': 'Organization',
            name: 'Masala Spin',
            url: SITE_URL,
            logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo192.png` },
          },
          mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/blog/${post.slug}` },
        }
      : null,
    noindex: !post,
  });

  if (!post) {
    return (
      <div className="min-h-screen" style={{ background: '#FDFBF7' }}>
        <SiteHeader />
        <main className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1C1917] mb-4">
            We could not find that article
          </h1>
          <button
            onClick={() => navigate('/blog')}
            data-testid="blog-not-found-back"
            className="px-6 py-3 bg-[#F97316] text-white rounded-full font-['Outfit']"
          >
            See all articles
          </button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#FDFBF7' }}>
      <SiteHeader />

      <main>
        <article className="max-w-3xl mx-auto px-6 py-12">
          <Breadcrumbs items={[{ name: 'Recipe Blog', to: '/blog' }, { name: post.title }]} />

          {post.region && (
            <p className="inline-flex items-center gap-2 px-3 py-1 bg-[#F97316]/10 text-[#F97316] rounded-full text-xs font-['Outfit'] font-semibold uppercase tracking-widest mb-4">
              <MapPin className="w-3 h-3" aria-hidden="true" /> {post.region}
            </p>
          )}

          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#1C1917] tracking-tight mb-4 leading-tight">
            {post.title}
          </h1>

          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-[#1C1917]/55 font-['Outfit'] mb-8">
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" aria-hidden="true" /> {post.readMinutes} min read
            </span>
            <span aria-hidden="true">·</span>
            <span>Published {fmt(post.date)}</span>
            {post.dateModified && post.dateModified !== post.date && (
              <>
                <span aria-hidden="true">·</span>
                <span>Updated {fmt(post.dateModified)}</span>
              </>
            )}
            <span aria-hidden="true">·</span>
            <span>By Masala Spin</span>
          </p>

          {post.cover && (
            <div className="relative h-[240px] sm:h-[380px] rounded-3xl overflow-hidden shadow-xl mb-10">
              <DishImage
                src={post.cover}
                alt={`${post.title} — Masala Spin`}
                fallbackInitial={post.title[0]}
                className="w-full h-full"
                imgClassName="w-full h-full object-cover"
                loading="eager"
                fetchPriority="high"
              />
            </div>
          )}

          {post.disclaimerText && (
            <aside
              data-testid="health-disclaimer"
              className="mb-10 p-5 rounded-2xl border border-[#F97316]/30 bg-[#F97316]/5"
              aria-label="Health and nutrition disclaimer"
            >
              <p className="flex items-start gap-2.5 font-['Outfit'] text-sm text-[#1C1917]/80 leading-relaxed">
                <ShieldAlert className="w-5 h-5 text-[#F97316] mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span>{post.disclaimerText}</span>
              </p>
            </aside>
          )}

          <div className="font-['Outfit']">
            <Markdown>{post.body}</Markdown>
          </div>

          {linkedRecipes.length > 0 && (
            <section className="mt-14 pt-10 border-t border-[#1C1917]/10">
              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1C1917] mb-6">
                Recipes mentioned in this article
              </h2>
              <ul className="grid sm:grid-cols-2 gap-4">
                {linkedRecipes.map((r) => (
                  <li key={r.id || r.name}>
                    <Link
                      to={`/recipe/${slugify(r.name)}`}
                      data-testid={`linked-recipe-${slugify(r.name)}`}
                      className="flex items-center gap-3 p-3 rounded-xl bg-white border border-[#1C1917]/5 hover:border-[#F97316]/40 hover:shadow-md transition-all h-full"
                    >
                      <span className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 block">
                        <DishImage
                          src={r.image_url}
                          alt={`${r.name} recipe`}
                          fallbackInitial={r.name?.[0]}
                          className="w-full h-full"
                          imgClassName="w-full h-full object-cover"
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="block font-['Playfair_Display'] font-bold text-[#1C1917] text-sm leading-tight">
                          {r.name}
                        </span>
                        <span className="block text-xs text-[#1C1917]/55 font-['Outfit'] mt-0.5">
                          {r.prep_time} · ~{r.calories} kcal
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="mt-14 pt-10 border-t border-[#1C1917]/10">
            <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1C1917] mb-6">
              More from the blog
            </h2>
            <ul className="grid sm:grid-cols-2 gap-4">
              {POSTS.filter((p) => p.slug !== post.slug).map((p) => (
                <li key={p.slug}>
                  <Link
                    to={`/blog/${p.slug}`}
                    data-testid={`related-post-${p.slug}`}
                    className="block h-full p-4 rounded-xl bg-white border border-[#1C1917]/5 hover:border-[#F97316]/40 transition-colors"
                  >
                    <span className="block font-['Playfair_Display'] font-bold text-[#1C1917] mb-1 leading-tight">
                      {p.title}
                    </span>
                    <span className="block text-xs text-[#1C1917]/55 font-['Outfit'] line-clamp-2">
                      {p.description}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        </article>
      </main>

      <SiteFooter />
    </div>
  );
}
